import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Sidebar } from "primereact/sidebar";
import * as Yup from "yup";
import useAuthHttp from "../hooks/useAuthHttp";
import { Toast } from "primereact/toast";
import { useRef } from "react";

interface IAcceptPolicyAndTermsDialogProps {
    show: boolean;
    onClose: (termsAccepted: boolean) => void;
    termsUrl: string;
    privacyUrl: string;
}

export default function AcceptPolicyAndTermsDialog({ show, onClose, termsUrl, privacyUrl }: IAcceptPolicyAndTermsDialogProps) {
    const authHttp = useAuthHttp();
    const toast = useRef<Toast | null>(null);
    const acceptTermsMutation = useMutation({
        mutationFn: async () => {
            return await authHttp.doPost<any, { success: boolean; }>(`user/terms-acceptance`, {});
        },
        onSuccess: () => {
            toast.current?.show({
                severity: "success",
                summary: "Termos de Uso e Política de Privacidade aceitos com sucesso",
            });
        },
        onError: (error: Error) => {
            toast.current?.show({
                severity: "error",
                summary: "Falha ao aceitar os Termos de Uso e Política de Privacidade",
                detail: error.message ?? "Por favor, tente novamente mais tarde."
            });
        }
    });
    
    const termsFormik = useFormik({
        initialValues: {
            acceptTerms: false
        },
        validationSchema: Yup.object().shape({
            acceptTerms: Yup.boolean().oneOf([true], "Você precisa aceitar os Termos de Uso e Política de Privacidade")
        }),
        onSubmit: async (values) => {
            const acceptTermsResult = await acceptTermsMutation.mutateAsync();

            if (!acceptTermsResult?.result?.success) {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao aceitar os Termos de Uso e Política de Privacidade",
                    detail: "Por favor, tente novamente mais tarde."
                });

                return;
            }

            onClose(values.acceptTerms);
            termsFormik.resetForm();
        }
    })
    return (
        <>
            <Toast ref={toast} />
            <Sidebar 
                fullScreen 
                header={<h3>Para continuar, você precisa aceitar os Termos de Uso e Política de Privacidade</h3>}
                visible={show} 
                onHide={() => {
                    onClose(false);
                    termsFormik.resetForm();
                }}
                closeOnEscape={false}
                showCloseIcon={false}
            >
                <div style={{ width: "100%", height: "2850px", overflow: "hidden", position: "relative" }}>
                    <iframe 
                        src={privacyUrl ?? "https://www.wings.money/politica"}
                        style={{ 
                            width: "100%", 
                            height: "100%", 
                            border: "none",
                            pointerEvents: "none"
                        }} 
                        scrolling="no"
                    />
                </div>
                <div style={{ width: "100%", height: "2950px", overflow: "hidden", position: "relative" }}>
                    <iframe 
                        src={termsUrl ?? "https://www.wings.money/termos"}
                        style={{ 
                            width: "100%", 
                            height: "100%", 
                            border: "none",
                            pointerEvents: "none"
                        }} 
                        scrolling="no"
                    />
                </div>

                <div className="flex flex-column gap-2 justify-content-end align-items-end">
                    <div className="flex align-items-center mb-2 mt-4">
                        <Checkbox 
                            id="accept-terms" 
                            name="accept-terms" 
                            value="accept-terms" 
                            onChange={(e) => termsFormik.setFieldValue("acceptTerms", e.target.checked)} 
                            checked={termsFormik.values.acceptTerms} 
                            invalid={!!termsFormik.errors.acceptTerms && termsFormik.touched.acceptTerms}
                        />
                        <label htmlFor="accept-terms" className="ml-2">Eu li e aceito os Termos de Uso e Política de Privacidade</label>
                    </div>

                    <Button 
                        label="Aceito" 
                        onClick={() => {
                            termsFormik.setFieldValue("acceptTerms", true);
                            termsFormik.handleSubmit();
                        }} 
                        disabled={acceptTermsMutation.isPending}
                        loading={acceptTermsMutation.isPending}
                    />
                </div>
            </Sidebar>
        </>
    )
}